import { useState, useEffect, useCallback, type ReactNode } from "react";
import { motion } from "framer-motion";
import type { Categorization } from "../types/transaction";
import { formatAccountName } from "../utils/accounts";
import { formatAmount, isNegativeAmount } from "../utils/currency";

export interface SplitLine {
  account: string;
  /** Amount in integer cents for precision. */
  amountCents: number;
  /** Display string for the input field. */
  displayAmount: string;
}

interface SplitModalProps {
  item: Categorization;
  accounts: string[];
  onClose: () => void;
  onSplit: (lines: { account_name: string; amount: string }[]) => void;
}

/** Convert a dollar string to integer cents, avoiding floating-point errors. */
function dollarsToCents(value: string): number {
  if (!value) return 0;
  // Remove any non-numeric chars except dot and minus
  const cleaned = value.replace(/[^0-9.-]/g, "");
  if (!cleaned) return 0;
  // Split on decimal point and handle each part as integers
  const parts = cleaned.split(".");
  const wholePart = parseInt(parts[0] ?? "0", 10) || 0;
  const fracStr = (parts[1] ?? "").padEnd(2, "0").slice(0, 2);
  const fracPart = parseInt(fracStr, 10) || 0;
  const sign = cleaned.startsWith("-") ? -1 : 1;
  return sign * (Math.abs(wholePart) * 100 + fracPart);
}

/** Convert integer cents to a dollar display string. */
function centsToDollars(cents: number): string {
  const sign = cents < 0 ? "-" : "";
  const absCents = Math.abs(cents);
  const dollars = Math.floor(absCents / 100);
  const remainder = absCents % 100;
  return `${sign}${dollars}.${String(remainder).padStart(2, "0")}`;
}

/** Convert integer cents to a Decimal-safe string for the API. */
function centsToDecimalString(cents: number): string {
  return centsToDollars(cents);
}

export function SplitModal({ item, accounts, onClose, onSplit }: SplitModalProps): ReactNode {
  const totalCents = item.amount !== null ? Math.abs(dollarsToCents(item.amount)) : 0;
  const isNegative = isNegativeAmount(item.amount);

  const [lines, setLines] = useState<SplitLine[]>([
    {
      account: item.account_name,
      amountCents: totalCents,
      displayAmount: centsToDollars(totalCents),
    },
  ]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const totalAllocatedCents = lines.reduce((sum, l) => sum + l.amountCents, 0);
  const remainingCents = totalCents - totalAllocatedCents;
  const isBalanced = remainingCents === 0;
  const hasEmptyAccounts = lines.some((l) => !l.account);
  const hasZeroAmounts = lines.some((l) => l.amountCents === 0);
  const canSave =
    isBalanced && !hasEmptyAccounts && !hasZeroAmounts && lines.length >= 2 && !saving;

  function addLine(): void {
    const prefilled = remainingCents > 0 ? remainingCents : 0;
    setLines([
      ...lines,
      {
        account: "",
        amountCents: prefilled,
        displayAmount: prefilled > 0 ? centsToDollars(prefilled) : "",
      },
    ]);
  }

  const handleEscape = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    },
    [onClose],
  );

  useEffect(() => {
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [handleEscape]);

  function updateLine(index: number, field: "account" | "amount", value: string): void {
    const updated = [...lines];
    const line = updated[index];
    if (!line) return;
    if (field === "account") {
      line.account = value;
    } else {
      const cents = dollarsToCents(value);
      line.amountCents = cents;
      line.displayAmount = value;
    }
    setLines(updated);
  }

  function removeLine(index: number): void {
    if (lines.length <= 1) return;
    setLines(lines.filter((_, i) => i !== index));
  }

  function handleSave(): void {
    if (!canSave) return;
    setError("");
    setSaving(true);

    // Convert to API format: amounts as Decimal strings
    // Original amount is negative for expenses, so split amounts should match the sign
    const apiLines = lines.map((l) => ({
      account_name: l.account,
      amount: centsToDecimalString(isNegative ? -l.amountCents : l.amountCents),
    }));

    onSplit(apiLines);
  }

  const remainingDollars = centsToDollars(Math.abs(remainingCents));

  return (
    <motion.div
      className="split-modal-backdrop"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <motion.div
        className="split-modal"
        initial={{ opacity: 0, y: 40, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 40, scale: 0.95 }}
        transition={{ type: "spring", stiffness: 300, damping: 25 }}
      >
        <div className="split-modal-header">
          <h3>Split transaction</h3>
          <button type="button" className="split-modal-close" onClick={onClose} aria-label="Close">
            &#10005;
          </button>
        </div>

        <div className="split-modal-total">
          <span className="split-modal-amount">
            {item.amount !== null ? formatAmount(item.amount.replace(/^-/, "")) : "--"}
          </span>
          <span className="split-modal-desc">{item.description}</span>
        </div>

        {error && <div className="split-modal-error">{error}</div>}

        <div className="split-modal-lines">
          {lines.map((line, index) => (
            <div key={index} className="split-line">
              <select
                value={line.account}
                onChange={(e) => updateLine(index, "account", e.target.value)}
                className="split-line-account"
                aria-label={`Account for split line ${index + 1}`}
              >
                <option value="">Select account...</option>
                {accounts.map((a) => (
                  <option key={a} value={a}>
                    {formatAccountName(a)}
                  </option>
                ))}
              </select>
              <div className="split-line-amount-wrap">
                <span className="split-line-dollar">$</span>
                <input
                  type="number"
                  value={line.displayAmount}
                  onChange={(e) => updateLine(index, "amount", e.target.value)}
                  className="split-line-amount"
                  aria-label={`Amount for split line ${index + 1}`}
                  placeholder="0.00"
                  step="0.01"
                  min="0"
                />
              </div>
              {lines.length > 1 && (
                <button
                  type="button"
                  className="split-line-remove"
                  onClick={() => removeLine(index)}
                  aria-label={`Remove split line ${index + 1}`}
                >
                  &#10005;
                </button>
              )}
            </div>
          ))}
        </div>

        <button type="button" className="split-add-line" onClick={addLine}>
          + Add line
        </button>

        <div
          className={
            "split-remaining" +
            (isBalanced ? " split-balanced" : remainingCents < 0 ? " split-over" : "")
          }
        >
          {isBalanced
            ? "Balanced"
            : remainingCents > 0
              ? `$${remainingDollars} unallocated`
              : `$${remainingDollars} over-allocated`}
        </div>

        {lines.length < 2 && (
          <div className="split-hint">Add at least two lines to split this transaction.</div>
        )}

        <div className="split-modal-actions">
          <button type="button" className="split-cancel" onClick={onClose}>
            Cancel
          </button>
          <motion.button
            type="button"
            className="split-save"
            onClick={handleSave}
            whileHover={canSave ? { scale: 1.02 } : {}}
            whileTap={canSave ? { scale: 0.98 } : {}}
            disabled={!canSave}
          >
            {saving ? "Splitting..." : "Split transaction"}
          </motion.button>
        </div>
      </motion.div>
    </motion.div>
  );
}
